import { useCallback, useEffect, useRef, useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { instance } from "api/apiBase";
import { toast } from "react-toastify";
import { useAuthContext } from "providers/Auth";
import type { ColumnVisibilityItem } from "providers/Auth";
import { getErrorMessage } from "utils/error";

const mergeColumnsWithDefaults = (
  savedColumns: ColumnVisibilityItem[] | undefined,
  defaults: ColumnVisibilityItem[]
): ColumnVisibilityItem[] => {
  if (!savedColumns?.length) return defaults;

  const defaultsById = new Map(defaults.map((c) => [c.id, c]));

  const merged: ColumnVisibilityItem[] = savedColumns
    .filter((c) => defaultsById.has(c.id))
    .map((c) => {
      const def = defaultsById.get(c.id);
      // Preserve user's checked + order, but pull always_on / disabled / label
      // from defaults so the canonical metadata stays in sync.
      return {
        ...c,
        label: def?.label ?? c.label,
        always_on: def?.always_on,
        disabled: def?.disabled ?? c.disabled,
        checked: def?.always_on ? true : c.checked
      };
    });

  let insertAfterIndex = -1;
  defaults.forEach((def) => {
    const mergedIndex = merged.findIndex((c) => c.id === def.id);
    if (mergedIndex !== -1) {
      insertAfterIndex = Math.max(insertAfterIndex, mergedIndex);
      return;
    }
    merged.splice(insertAfterIndex + 1, 0, def);
    insertAfterIndex += 1;
  });

  return merged;
};

const columnsEqual = (
  a: ColumnVisibilityItem[],
  b: ColumnVisibilityItem[]
): boolean => {
  if (a.length !== b.length) return false;
  return a.every(
    (col, i) => col.id === b[i].id && col.checked === b[i].checked
  );
};

/**
 * Manages per-user, per-tab column visibility + ordering.
 *
 * Two save modes:
 *   - `flush()`        — save-on-close (call when the menu closes); skips the
 *                        network request if nothing changed since the last save
 *   - `commitNow(next)`— update state AND save immediately (for in-table
 *                        interactions like hide-column or drag-reorder)
 */
export const useColumnVisibility = (
  tabKey: string,
  defaults: ColumnVisibilityItem[]
) => {
  const { auth, refreshAuthUserDashboardState } = useAuthContext();

  const [columns, setColumns] = useState<ColumnVisibilityItem[]>(() =>
    mergeColumnsWithDefaults(auth?.columns_visibility?.[tabKey], defaults)
  );
  const lastSavedRef = useRef<ColumnVisibilityItem[]>(columns);

  useEffect(() => {
    const next = mergeColumnsWithDefaults(
      auth?.columns_visibility?.[tabKey],
      defaults
    );
    setColumns(next);
    lastSavedRef.current = next;
  }, [tabKey, auth?.columns_visibility, defaults]);

  const updateMutation = useMutation({
    mutationKey: ["UPDATE_COLUMNS_VISIBILITY", auth?.id, tabKey],
    mutationFn: (allTabs: Record<string, ColumnVisibilityItem[]>) =>
      instance.patch(`/portal/v1/users/${auth?.id}`, {
        columns_visibility: allTabs
      })
  });

  const save = useCallback(
    async (nextColumns: ColumnVisibilityItem[]) => {
      if (columnsEqual(nextColumns, lastSavedRef.current)) return;

      const payload = {
        ...(auth?.columns_visibility ?? {}),
        [tabKey]: nextColumns
      };

      try {
        await updateMutation.mutateAsync(payload);
        lastSavedRef.current = nextColumns;
        refreshAuthUserDashboardState();
      } catch (error) {
        toast.error(
          `Failed to update column visibility: ${getErrorMessage(error)}`
        );
      }
    },
    [
      auth?.columns_visibility,
      tabKey,
      updateMutation,
      refreshAuthUserDashboardState
    ]
  );

  const flush = useCallback(() => save(columns), [columns, save]);

  const commitNow = useCallback(
    (nextColumns: ColumnVisibilityItem[]) => {
      setColumns(nextColumns);
      return save(nextColumns);
    },
    [save]
  );

  return {
    columns,
    setColumns,
    flush,
    commitNow
  };
};
